import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from '../Dashboard/index.module.css';
import { useDispatch, useSelector } from "react-redux";
import { addNewSkill, editSkill, getAllSkills } from "../../../redux/features/skill/skillService";
import { showToast } from "../../../components/ToastMsg";
import BeatLoader from "react-spinners/BeatLoader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faCircleXmark } from "@fortawesome/free-regular-svg-icons";
import Modal from "../../../components/Modal";
import Pagination from "../../../components/Pagination";
import NoResultsFound from "../../../components/NoResultsFound";
import TableSkeleton from "../../../components/Skeleton";
import { editUser } from "../../../redux/features/users/userService";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import SearchBarOnChange from "../../../components/SearchBarOnChange";

const Skill = () => {

    const dispatch = useDispatch();
    const roleDropdownRef = useRef(null);

    const { isSkillsLoading, pageCount, skills } = useSelector((state) => state.skill.allSkills);
    const { isSkillCreating } = useSelector((state) => state.skill.newSkill);
    const { isEditingSkill } = useSelector((state) => state.skill.editedSkill);

    const [formData, setFormData] = useState({
        name: '',
    });

    const [errorMessages, setErrorMessages] = useState({});
    const [disabledBtn, setBtnDisable] = useState(false);
    const [skip, setSkip] = useState(0);

    const limit = 10;  // Number of items per page
    const [isModalOpen, setModalOpen] = useState(false);
    const [selectedSkill, setSelectedSkill] = useState(null);
    const [initialSkill, setInitialSkill] = useState(null);
    const [editErrorMsgs, setEditErrorMsgs] = useState({
        name: '',
    });

    const [disabledEditBtn, setEditBtnDisable] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    const fetchAllSkills = useCallback(() => {
        dispatch(getAllSkills({
            skip: skip,
            limit,
            search: searchQuery,
        }));
    }, [dispatch, skip, searchQuery]);

    useEffect(() => {
        fetchAllSkills();
    }, [fetchAllSkills]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        setErrorMessages((prev) => ({
            ...prev,
            [name]: '',
        }));
    };

    // Validate input
    const isValidSkill = () => {
        const errors = {};
        const trimmedName = formData.name.trim();
        if (!trimmedName) {
            errors.name = "Name is required.";
        }
        if (Object.keys(errors).length > 0) {
            setErrorMessages(errors);
            return false;
        } else {
            return true;
        }
    };

    const insertNewSkill = async () => {
        if (isValidSkill()) {
            setBtnDisable(true);
            try {
                const response = await dispatch(addNewSkill(formData)).unwrap();
                setBtnDisable(false);
                setFormData({
                    name: '',
                });
                if (roleDropdownRef.current) {
                    roleDropdownRef.current.clearSelection();
                }
                fetchAllSkills();
                showToast('success', response.message);
            }
            catch (error) {
                setBtnDisable(false);
                showToast('error', error.message);
            };
        }
    };

    // Edit skill
    const handleSelectedSkillChange = (e) => {
        const { name, value } = e.target;
        setErrorMsg(null);
        setSelectedSkill((prev) => ({
            ...prev,
            [name]: value,
        }));

        setEditErrorMsgs((prev) => ({
            ...prev,
            [name]: '',
        }));
    };

    const isValidEditSkill = () => {
        const errors = {};
        const trimmedName = selectedSkill.name.trim();

        if (!trimmedName) {
            errors.name = "Name is required.";
        }
        if (Object.keys(errors).length > 0) {
            setEditErrorMsgs(errors);
            return false;
        } else {
            return true;
        }
    };

    function getChangedValues(selectedData) {
        const changedValues = {};
        for (const key in selectedData) {
            if (
                key !== 'id' &&
                selectedData.hasOwnProperty(key) &&
                selectedData[key] !== initialSkill[key]
            ) {
                changedValues[key] = selectedData[key];
            }

            if (key === 'id') {
                changedValues[key] = selectedData[key];
            }
        }
        return changedValues;
    }

    const editSkillDetails = async () => {
        if (isValidEditSkill(selectedSkill)) {
            const data = getChangedValues(selectedSkill);
            const hasChanges = Object.keys(data).some(key => key !== 'id');
            if (!hasChanges) {
                setErrorMsg('There are no changes to update.');
            } else {
                setEditBtnDisable(true);
                try {
                    const response = await dispatch(editSkill(data)).unwrap();
                    setEditBtnDisable(false);
                    setSelectedSkill(null);
                    setInitialSkill(null);
                    setModalOpen(false);
                    showToast('success', response.message);
                    fetchAllSkills();
                }
                catch (error) {
                    setEditBtnDisable(false);
                    showToast('error', error.message);
                };
            }
        }
    };

    //Activate Skill
    const [isActivateModalOpen, setActivateModalOpen] = useState(false);
    const [isDeactivationModalOpen, setDeactivationModalOpen] = useState(false);
    const [selectedSkillId, setSelectedSkillId] = useState(null);

    const closeModal = () => setActivateModalOpen(false);
    const closDeactivationModal = () => setDeactivationModalOpen(false);

    const handleYes = async () => {
        setActivateModalOpen(false);
        try {
            const response = await dispatch(editSkill({ id: selectedSkillId, status: true })).unwrap();
            setSelectedSkillId(null);
            setInitialSkill(null);
            showToast('success', response.message);
            fetchAllSkills();
        }
        catch (error) {
            showToast('error', error.message);
        };
    }
    const handleRejectYes = async () => {
        setDeactivationModalOpen(false);
        try {
            const response = await dispatch(editSkill({ id: selectedSkillId, status: false })).unwrap();
            setSelectedSkillId(null);
            setInitialSkill(null);
            showToast('success', response.message);
            fetchAllSkills();
        }
        catch (error) {
            showToast('error', error.message);
        };
    }

    const handlePageChange = (pageNumber) => {
        setSkip(pageNumber - 1);
    };

    const handleSearch = (query) => {
        setSearchQuery(query)
        setSkip(0);
    };

    return (
        <div className={`card-section`}>
            <div className={`main-card`}>
                <h1 className={`form-title`}>Add New Skill</h1>
                <div className={styles.formContainer}>
                    <form
                        className={styles.coordinatorForm}
                        noValidate
                    >
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='name' className={`form-label`}>
                                        Name<span className={`required-star`}>*</span>
                                    </label>
                                    <input
                                        type='text'
                                        id='name'
                                        name='name'
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        placeholder='Enter Name'
                                        className={`form-input-default`}
                                    />
                                    {errorMessages.name && (
                                        <p className={`text-danger`}>{errorMessages.name}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <button
                            className={`btn-primary`}
                            type='button'
                            disabled={disabledBtn}
                            onClick={insertNewSkill}
                        >
                            {isSkillCreating ? <BeatLoader color='#ffffff' /> : `Create`}
                        </button>
                    </form>
                </div>
            </div>
            <div className={`search-card`}>
                <div className={`search-card-header`}>
                    <h1 className={`form-title`}>Skills</h1>
                    <SearchBarOnChange
                        value={searchQuery}
                        onSearch={handleSearch}
                    />
                </div>
                <div className={`search-card-body`}>
                    {isSkillsLoading ?
                        <TableSkeleton columns={["Name", "Status", "Actions"]} rowCount={10} height={20} />
                        : skills.length > 0 ?
                            <div className={`table-container`}>
                                <table className={`table`}>
                                    <thead className={`thead`}>
                                        <tr>
                                            <th className={`theadColumnOne`}>Name</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className={`tbody`}>
                                        {skills?.map((skill) => (
                                            <tr key={skill.id}>
                                                <td className={`tdataColumnOne`}>
                                                    {skill.name}
                                                </td>
                                                <td>{skill.status ? 'Active' : 'Inactive'}</td>
                                                <td className={`actionsBtn`}>
                                                    <button
                                                        className={skill.status ? `btn-inactive` : `btn-active`}
                                                        onClick={() => {
                                                            setSelectedSkillId(skill.id);
                                                            skill.status ? setDeactivationModalOpen(true) : setActivateModalOpen(true);
                                                        }
                                                        }>{skill.status ? 'Deactivate' : 'Acitvate'}</button>
                                                    <button
                                                        className={`btn-active`}
                                                        onClick={() => {
                                                            setSelectedSkill(skill);
                                                            setInitialSkill(skill);
                                                            setModalOpen(true);
                                                            setEditErrorMsgs({
                                                                name: ''
                                                            })
                                                        }
                                                        }>Edit</button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            :
                            <NoResultsFound />
                    }
                </div>
                <div className={`search-card-footer`}>
                    {pageCount > 0 && (
                        <div className={styles.paginationContainer}>
                            <Pagination
                                currentPage={skip + 1}
                                totalPages={pageCount}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    )}
                </div>
            </div>
            {isModalOpen &&
                <div className={`modal-overlay`}>
                    <div className={`modal-card`}>
                        <div className={`modal-header`}>
                            <h4 className={`modal-title`}>Edit Course Type</h4>
                            <FontAwesomeIcon icon={faXmark} onClick={() => setModalOpen(false)} size="lg" cursor="pointer" />
                        </div>
                        <div className={`modal-body`}>
                            <div className={styles.formContainer}>
                                <form
                                    className={styles.coordinatorForm}
                                    noValidate
                                >
                                    <div className={`form-input-row md-col`}>
                                        <div className={`form-input-group`}>
                                            <label htmlFor='full_name' className={`form-label`}>
                                                Name<span className={`required-star`}>*</span>
                                            </label>
                                            <input
                                                type='text'
                                                id='name'
                                                name='name'
                                                value={selectedSkill.name || ''}
                                                onChange={handleSelectedSkillChange}
                                                placeholder='Enter Name'
                                                className={`form-input-default`}
                                            />
                                            {editErrorMsgs.name && (
                                                <p className={`text-danger`}>{editErrorMsgs.name}</p>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        className={`btn-primary`}
                                        type='button'
                                        disabled={disabledEditBtn}
                                        onClick={editSkillDetails}
                                    >
                                        {isEditingSkill ? <BeatLoader vehicleType='#ffffff' /> : `Save`}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            }
            {isActivateModalOpen && (
                <Modal
                    title='Skill Activation'
                    onClose={closeModal}
                    onYes={handleYes}
                    isInput={false}
                    iconName={faCircleCheck}
                    bodyText={`Are you sure you want to activate the Skill?`}
                    colorType={`var(--green)`}
                />
            )}
            {isDeactivationModalOpen && (
                <Modal
                    title='Skill Deactivation'
                    onClose={closDeactivationModal}
                    onYes={handleRejectYes}
                    isInput={false}
                    iconName={faCircleXmark}
                    bodyText={`Are you sure you want to deactivate the Skill?`}
                    colorType={`var(--danger)`}
                />
            )}
        </div>
    );
};

export default Skill;